// heavyPanel.ts — the R-LAW-3 "one heavy panel at a time" primitive.
// CONTRACT: docs/ux/SHELL-COMPOSITION.md §4(5), §5 A10, §6 R-LAW-3
//
// THE LAW (R-LAW-3): exactly ONE heavy summoned surface may be open at any
// instant. The heavy surfaces are: the Living Spine's Catalog flip, the
// Throughline dropdown, the Ledger's relationship graph, and the Settings
// drawer. Summoning any one of them MUST collapse whichever was previously
// open. Light surfaces (the Verdict drill popover, a chip, the resting Ledger
// summary) do NOT participate — they never claim, so they never displace.
//
// This is a TINY framework-agnostic store (no React, no host, no MapLibre). A
// component imports `useHeavyPanel(id)` for the ergonomic React binding, or the
// raw `claimHeavy` / `releaseHeavy` / `subscribeHeavy` for non-hook callers.
// The store holds at most one active panel id; claiming a new one emits a
// displacement so the previously-open panel can tear down its own state.

/** The fixed set of HEAVY panels governed by R-LAW-3. Light surfaces are NOT
 *  members and must never call `claimHeavy`. Kept as a string-literal union so a
 *  typo can't invent a new heavy surface and silently escape the law. */
export type HeavyPanelId =
  | "catalog" // Living Spine +Add flip (A3)
  | "throughline" // Throughline summoned dropdown (A6)
  | "graph" // Ledger relationship graph / GraphView (A5)
  | "settings"; // SettingsDrawer (A9)

/** A subscriber is notified on EVERY change of the single active panel, with the
 *  new active id (or `null` when nothing is open). A panel uses this to detect
 *  that it was DISPLACED (active !== its own id) and collapse itself. */
export type HeavyPanelListener = (active: HeavyPanelId | null) => void;

// --- the store: at most one active heavy panel ---------------------------------

let active: HeavyPanelId | null = null;
const listeners = new Set<HeavyPanelListener>();

function emit(): void {
  // Snapshot to a local so a listener that (un)subscribes during dispatch can't
  // mutate the set we're iterating.
  for (const fn of [...listeners]) fn(active);
}

/** The currently-open heavy panel, or `null` if none. Synchronous read. */
export function getActiveHeavy(): HeavyPanelId | null {
  return active;
}

/** Claim the single heavy slot for `id`. If a DIFFERENT panel held it, that one
 *  is displaced (subscribers see the new active id and self-collapse). Idempotent
 *  for the already-active id (no redundant emit). Returns the displaced id, if
 *  any, for callers that want to react inline. */
export function claimHeavy(id: HeavyPanelId): HeavyPanelId | null {
  if (active === id) return null;
  const displaced = active;
  active = id;
  emit();
  return displaced;
}

/** Release the heavy slot — but ONLY if `id` still holds it. This guards against
 *  a stale teardown (a panel that was already displaced by a newer claim must not
 *  null out the newcomer's claim). Releasing a panel that isn't active is a no-op. */
export function releaseHeavy(id: HeavyPanelId): void {
  if (active !== id) return;
  active = null;
  emit();
}

/** Subscribe to active-panel changes. Returns an unsubscribe fn. The listener is
 *  NOT invoked on subscribe (read `getActiveHeavy()` for the initial value). */
export function subscribeHeavy(fn: HeavyPanelListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// --- the React binding ---------------------------------------------------------

import { useEffect, useState } from "react";

/** What `useHeavyPanel` hands back to a heavy-panel component. */
export interface HeavyPanelControls {
  /** True iff THIS panel currently owns the single heavy slot. Drives `open`. */
  isActive: boolean;
  /** The active panel id across the whole shell (or null) — for callers that
   *  want to know WHO holds it, not just whether they do. */
  activeId: HeavyPanelId | null;
  /** Claim the slot for this panel (collapses any other heavy surface). */
  claim: () => void;
  /** Release the slot if this panel holds it. */
  release: () => void;
  /** Convenience: claim when `next` is true, release when false. */
  setActive: (next: boolean) => void;
}

/**
 * Bind a heavy-panel component to the R-LAW-3 slot.
 *
 * Usage in a heavy surface (e.g. the Catalog flip):
 *   const heavy = useHeavyPanel("catalog");
 *   // open the surface:   heavy.claim()  (or heavy.setActive(true))
 *   // render gated on:     heavy.isActive
 *   // when displaced by another panel, `isActive` flips to false → collapse.
 *
 * The hook subscribes to the shared store and re-renders the owner whenever the
 * active panel changes, so being DISPLACED by a sibling claim closes this panel
 * automatically. On unmount it releases the slot iff it still holds it.
 */
export function useHeavyPanel(id: HeavyPanelId): HeavyPanelControls {
  const [activeId, setActiveId] = useState<HeavyPanelId | null>(() => getActiveHeavy());

  useEffect(() => {
    // Re-sync immediately in case the store changed between render and effect.
    setActiveId(getActiveHeavy());
    const unsub = subscribeHeavy(setActiveId);
    return () => {
      unsub();
      // Defensive: if this panel is being unmounted while it still holds the
      // slot, free it so the law isn't left stuck on a dead component.
      releaseHeavy(id);
    };
  }, [id]);

  return {
    isActive: activeId === id,
    activeId,
    claim: () => claimHeavy(id),
    release: () => releaseHeavy(id),
    setActive: (next: boolean) => {
      if (next) claimHeavy(id);
      else releaseHeavy(id);
    },
  };
}
